// src/invoicelist.js
import React, { useEffect, useMemo, useState } from 'react';
import { Eye } from 'lucide-react';
import { getApiBase } from './apiBase';
import { getTenantHeaders } from './api';

const toStartOfDay = (d) => {
  const x = new Date(d);
  x.setHours(0, 0, 0, 0);
  return x;
};

const parseDate = (v) => {
  if (!v) return null;
  const s = String(v);
  const d = new Date(s.includes('T') ? s : `${s}T00:00:00`);
  return isNaN(d.getTime()) ? null : d;
};

const fmtShortDate = (v) => {
  const d = parseDate(v);
  if (!d) return '';
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  return `${mm}/${dd}/${yyyy}`;
};

const money = (n) => Number(n || 0).toLocaleString('en-US', { style: 'currency', currency: 'USD' });

const viewedTitle = (value) => {
  const viewedAt = new Date(value);
  if (Number.isNaN(viewedAt.getTime())) return 'Public link viewed';
  return `Public link first viewed: ${viewedAt.toLocaleString()}`;
};

const toCents = (v) => {
  const num = Number(String(v ?? '').replace(/[^\d.-]/g, ''));
  if (!Number.isFinite(num)) return 0;
  return Math.round(num * 100);
};

const fromCents = (c) => (Number(c || 0) / 100);

const getOutstandingCents = (inv) => {
  if (!inv) return 0;
  const fallback = Number(inv.total || 0) - Number(inv.amount_paid || 0);
  return Math.max(0, toCents(inv.balance_due ?? fallback));
};

const getCreatedAt = (inv) => {
  return inv.created_at || inv.createdAt || inv.date_created || inv.dateCreated || inv.created || null;
};

const getInvoiceDate = (inv) => {
  return inv.invoice_date || inv.invoiceDate || getCreatedAt(inv) || null;
};

const getDueDate = (inv) => {
  return inv.due_date || inv.dueDate || inv.due || null;
};

const deriveStatus = (inv) => {
  const total = Number(inv.total || 0);
  const paid = Number(inv.amount_paid || inv.paid_amount || 0);
  const balance = Number(inv.balance_due ?? Math.max(0, total - paid));

  if ((inv.status || '').toLowerCase() === 'paid' || balance <= 0) return 'paid';

  const createdAt = parseDate(getCreatedAt(inv)) || new Date();
  const due = parseDate(getDueDate(inv)) || createdAt;
  const today = toStartOfDay(new Date());
  const dueDay = toStartOfDay(due);

  if (today.getTime() > dueDay.getTime() && balance > 0) return 'overdue';
  if (paid > 0 && balance > 0) return 'partial';

  const s = (inv.status || '').toLowerCase();
  if (s === 'sent') return 'sent';

  return 'unpaid';
};

const statusLabel = (s) => {
  if (s === 'paid') return 'PAID';
  if (s === 'overdue') return 'OVERDUE';
  if (s === 'partial') return 'PARTIAL';
  if (s === 'sent') return 'SENT';
  return 'UNPAID';
};

const badgeStyle = (status) => {
  if (status === 'paid')    return { backgroundColor: '#39FF14', color: 'black', border: '3px solid black' };
  if (status === 'overdue') return { backgroundColor: 'orange',  color: 'black', border: '3px solid black' };
  if (status === 'partial') return { backgroundColor: 'yellow',  color: 'black', border: '3px solid black' };
  if (status === 'sent')    return { backgroundColor: '#5aa7ff', color: 'black', border: '3px solid black' };
  return { backgroundColor: 'red', color: 'white', border: '3px solid black' };
};

const InvoiceList = ({
  invoices,
  onEdit,
  onDelete,
  onViewPrint,
  onRecordPayment,
  onPaymentReversed,
  onConvertBackToEstimate,
  onDuplicate,
  listControls,
  isGlobalSearchActive,
  currentUser
}) => {
  const [activeMenu, setActiveMenu]   = useState(null);
  const [showPayModal, setShowPayModal] = useState(false);
  const [sendingLink, setSendingLink] = useState(false);
  const [sendMsg, setSendMsg]           = useState('');
  const [copiedPublicUrl, setCopiedPublicUrl] = useState('');
  const [viewedPopupText, setViewedPopupText] = useState('');

  const [payData, setPayData] = useState({ method: 'Cash', ref: '', amount: '' });
  const [paymentError, setPaymentError] = useState('');
  const [paymentSubmitting, setPaymentSubmitting] = useState(false);
  const [paymentOutstandingCents, setPaymentOutstandingCents] = useState(null);

  const [showReverseModal, setShowReverseModal] = useState(false);
  const [reverseReason,    setReverseReason]    = useState('');
  const [reverseBy,        setReverseBy]        = useState('');

  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteReason,    setDeleteReason]    = useState('');
  const [restoreInventory, setRestoreInventory] = useState(null);

  const [showConvertBackModal, setShowConvertBackModal] = useState(false);
  const [showCheckoutModal,    setShowCheckoutModal]    = useState(false);
  const [checkoutAmount,       setCheckoutAmount]       = useState('');
  const [checkoutFeedback,     setCheckoutFeedback]     = useState(null);
  const [activeCheckoutConflict, setActiveCheckoutConflict] = useState(null);
  const [checkoutSubmitting,   setCheckoutSubmitting]   = useState(false);
  const [checkoutCanceling,    setCheckoutCanceling]    = useState(false);

  const hasInventorySelectedParts = Array.isArray(activeMenu?.items) && activeMenu.items.some((item) => {
    const sourceId = Number(item?.source_id);
    return item?.type === 'parts_inventory' && Number.isSafeInteger(sourceId) && sourceId > 0;
  });
  const canConfirmDelete = Boolean(deleteReason.trim()) &&
    (!hasInventorySelectedParts || typeof restoreInventory === 'boolean');

  const styles = {
    listSurface: {
      width: '100%',
      padding: '12px',
      backgroundColor: '#B7410E',
      boxSizing: 'border-box'
    },
    card: {
      border: '6px solid black',
      padding: '20px',
      backgroundColor: 'white',
      marginBottom: '15px',
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center'
    },
    badge: (status) => ({
      padding: '8px 15px',
      fontWeight: 900,
      textTransform: 'uppercase',
      fontSize: '14px',
      ...badgeStyle(status)
    }),
    overlay: {
      position: 'fixed',
      top: 0, left: 0, right: 0, bottom: 0,
      backgroundColor: 'rgba(0,0,0,0.9)',
      zIndex: 2000,
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      padding: '12px'
    },
    modal: {
      backgroundColor: 'white',
      border: '10px solid black',
      padding: '30px',
      width: '100%',
      maxWidth: '520px'
    },
    btn: {
      width: '100%',
      padding: '15px',
      marginBottom: '10px',
      fontWeight: 900,
      border: '4px solid black',
      cursor: 'pointer',
      textTransform: 'uppercase',
      fontSize: '16px',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      gap: '10px'
    },
    input: {
      padding: '12px',
      border: '4px solid black',
      fontWeight: 900,
      width: '100%',
      fontSize: '16px',
      boxSizing: 'border-box'
    },
    manageBtn: {
      backgroundColor: 'black',
      color: '#FFA500',
      padding: '12px 25px',
      border: '4px solid #E8761D',
      fontWeight: 900,
      cursor: 'pointer'
    },
    viewedBadge: {
      display: 'inline-flex',
      alignItems: 'center',
      gap: '6px',
      backgroundColor: '#ff1f1f',
      color: 'white',
      padding: '10px 14px',
      border: '3px solid black',
      borderRadius: '8px',
      fontWeight: 900,
      fontSize: '14px',
      whiteSpace: 'nowrap',
      cursor: 'pointer',
      flexShrink: 0
    },
    sharePopup: {
      position: 'fixed',
      top: '50%',
      left: '50%',
      transform: 'translate(-50%, -50%)',
      zIndex: 2100,
      width: 'calc(100% - 32px)',
      maxWidth: '440px',
      boxSizing: 'border-box',
      backgroundColor: 'white',
      border: '8px solid black',
      boxShadow: '0 0 0 6px #E8761D',
      padding: '16px'
    },
    shareDimmer: {
      position: 'fixed',
      inset: 0,
      zIndex: 2050,
      backgroundColor: 'rgba(0,0,0,0.65)'
    },
    viewedPopupOverlay: {
      position: 'fixed',
      inset: 0,
      zIndex: 2200,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      padding: '16px',
      backgroundColor: 'rgba(0,0,0,0.65)'
    },
    viewedPopup: {
      width: '100%',
      maxWidth: '380px',
      boxSizing: 'border-box',
      backgroundColor: 'white',
      border: '6px solid black',
      boxShadow: '0 0 0 5px #ff1f1f',
      padding: '20px',
      textAlign: 'center'
    }
  };

  useEffect(() => {
    if (!activeMenu) return undefined;

    const previousBodyOverflow = document.body.style.overflow;
    const previousRootOverflow = document.documentElement.style.overflow;

    document.body.style.overflow = 'hidden';
    document.documentElement.style.overflow = 'hidden';

    return () => {
      document.body.style.overflow = previousBodyOverflow;
      document.documentElement.style.overflow = previousRootOverflow;
    };
  }, [activeMenu]);

  const displayInvoices = useMemo(() => {
    return (invoices || []).map(inv => ({
      ...inv,
      _display_status: deriveStatus(inv)
    }));
  }, [invoices]);

  const submitPayment = async () => {
    if (!activeMenu || paymentSubmitting) return;

    setPaymentError('');

    const amtCents = toCents(payData.amount);
    if (!Number.isFinite(amtCents) || amtCents <= 0) {
      setPaymentError('Enter a payment amount.');
      return;
    }

    const outstandingCents = paymentOutstandingCents ?? getOutstandingCents(activeMenu);
    if (amtCents > outstandingCents) {
      setPaymentError(`Payment cannot exceed the outstanding balance of ${money(fromCents(outstandingCents))}.`);
      return;
    }

    const totalCents       = toCents(activeMenu.total);
    const alreadyPaidCents = toCents(activeMenu.amount_paid || 0);
    const newPaidCents     = alreadyPaidCents + amtCents;
    const newBalanceCents  = Math.max(0, totalCents - newPaidCents);

    const updatedInvoice = {
      ...activeMenu,
      expected_amount_paid: fromCents(alreadyPaidCents),
      amount_paid:         fromCents(newPaidCents),
      balance_due:         fromCents(newBalanceCents),
      last_payment_amount: fromCents(amtCents),
      last_payment_method: payData.method,
      last_payment_ref:    payData.ref,
      last_payment_date:   new Date().toISOString(),
      status: (newBalanceCents <= 0 && totalCents > 0) ? 'paid' : 'partial'
    };

    setPaymentSubmitting(true);
    try {
      const result = typeof onRecordPayment === 'function'
        ? await onRecordPayment(updatedInvoice)
        : { ok: false, error: 'Payment could not be recorded.' };

      if (!result?.ok) {
        if (result?.outstanding_cents !== undefined) {
          setPaymentOutstandingCents(Number(result.outstanding_cents));
        }
        setPaymentError(result?.error || 'Payment could not be recorded.');
        return;
      }

      const acceptedInvoice = {
        ...updatedInvoice,
        amount_paid: Number(result.amount_paid),
        expected_amount_paid: Number(result.amount_paid),
        balance_due: Number(result.balance_due),
        status: String(result.status || '').toLowerCase()
      };

      setShowPayModal(false);
      setPayData({ method: 'Cash', ref: '', amount: '' });
      setPaymentError('');
      setPaymentOutstandingCents(null);
      setActiveMenu(acceptedInvoice);
      window.dispatchEvent(new CustomEvent('jobflow:saved', {
        detail: { message: 'PAYMENT RECORDED!' }
      }));
    } catch (e) {
      setPaymentError(e?.message || 'Payment could not be recorded.');
    } finally {
      setPaymentSubmitting(false);
    }
  };

  const handleCopyLink = async () => {
    if (!activeMenu) return;
    setSendMsg('');
    setCopiedPublicUrl('');
    setSendingLink(true);
    try {
      const API = getApiBase();
      const id = activeMenu.external_id || activeMenu.id;
      const res = await fetch(`${API}/api/invoices/${encodeURIComponent(String(id))}/send`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...getTenantHeaders() }
      });
      const data = await res.json();
      if (!res.ok) {
        setCopiedPublicUrl('');
        setSendMsg(`❌ ${data.error || 'Failed to get link.'}`);
        return;
      }
      await navigator.clipboard.writeText(data.url);
      setSendMsg('✅ Invoice link copied to clipboard!');
      setCopiedPublicUrl(data.url);
    } catch (e) {
      setCopiedPublicUrl('');
      setSendMsg('❌ Failed to copy link — please try again.');
    } finally {
      setSendingLink(false);
    }
  };

  const handleTextCustomer = () => {
    if (!activeMenu || !copiedPublicUrl) return;
    const rawPhone = String(
      activeMenu.mobile_phone ||
      activeMenu.customer_phone ||
      activeMenu.office_phone ||
      ''
    ).trim();
    if (!rawPhone) {
      setSendMsg('NO CUSTOMER PHONE AVAILABLE.');
      return;
    }
    const normalizedPhone = rawPhone.startsWith('+')
      ? `+${rawPhone.slice(1).replace(/\D/g, '')}`
      : rawPhone.replace(/\D/g, '');
    if (!normalizedPhone) {
      setSendMsg('NO CUSTOMER PHONE AVAILABLE.');
      return;
    }
    const body = `Here is your invoice: ${copiedPublicUrl}`;
    window.location.href = `sms:${normalizedPhone}?body=${encodeURIComponent(body)}`;
  };

  const handleEmailCustomer = () => {
    if (!activeMenu || !copiedPublicUrl) return;
    const email = String(activeMenu.email || '').trim();
    if (!email) {
      setSendMsg('NO CUSTOMER EMAIL AVAILABLE.');
      return;
    }
    const invoiceNumber = String(activeMenu.invoice_number || '').trim();
    const customerName = String(activeMenu.customer_name || '').trim();
    const subject = `Invoice ${invoiceNumber}`.trim();
    const greeting = customerName ? `Hello ${customerName},` : 'Hello,';
    const body = `${greeting}\r\n\r\nHere is your invoice:\r\n\r\n${copiedPublicUrl}`;
    window.location.href = `mailto:${email}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
  };

  const handleTakePayment = async (overrideAmountCents) => {
    if (!activeMenu || checkoutSubmitting) return;
    setSendMsg('');
    setCheckoutFeedback(null);
    setActiveCheckoutConflict(null);
    setCheckoutSubmitting(true);
    try {
      const API = getApiBase();
      const id = activeMenu.external_id || activeMenu.id;
      const body = overrideAmountCents
        ? JSON.stringify({ amount_cents: overrideAmountCents })
        : undefined;
      const res = await fetch(`${API}/api/invoices/${encodeURIComponent(String(id))}/checkout`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...getTenantHeaders() },
        body
      });
      const data = await res.json();
      if (!res.ok) {
        const isCancelableConflict =
          data.code === 'ACTIVE_CHECKOUT_AMOUNT_CONFLICT' &&
          data.can_cancel_active_session === true;
        if (isCancelableConflict) setActiveCheckoutConflict(data);
        setCheckoutFeedback({ message: data.error || 'Failed to start checkout.', success: false });
        return;
      }
      if (data.url) {
        setShowCheckoutModal(false);
        setCheckoutFeedback(null);
        setActiveCheckoutConflict(null);
        window.open(data.url, '_blank');
      } else {
        setCheckoutFeedback({ message: 'No checkout URL returned.', success: false });
      }
    } catch (e) {
      setCheckoutFeedback({ message: 'Network error — please try again.', success: false });
    } finally {
      setCheckoutSubmitting(false);
    }
  };

  const cancelActiveCheckout = async () => {
    if (!activeMenu || !activeCheckoutConflict || checkoutCanceling) return;

    const activeAmount = money(fromCents(activeCheckoutConflict.active_session_amount_cents));
    const confirmed = window.confirm(
      `Cancel the existing ${activeAmount} checkout?\n\nIf someone currently has that payment page open, it will no longer accept payment.`
    );
    if (!confirmed) return;

    setCheckoutCanceling(true);
    setCheckoutFeedback(null);
    try {
      const API = getApiBase();
      const id = activeMenu.external_id || activeMenu.id;
      const res = await fetch(`${API}/api/invoices/${encodeURIComponent(String(id))}/checkout/cancel-active`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...getTenantHeaders() }
      });
      const data = await res.json();

      if (!res.ok || data.code !== 'ACTIVE_CHECKOUT_CANCELED') {
        setCheckoutFeedback({ message: data.error || 'Failed to cancel the active payment checkout.', success: false });
        return;
      }

      setActiveCheckoutConflict(null);
      setCheckoutFeedback({
        message: data.message || 'The previous payment checkout was canceled. Choose the payment amount again.',
        success: true
      });
    } catch (e) {
      setCheckoutFeedback({ message: 'Network error — the active payment checkout was not canceled.', success: false });
    } finally {
      setCheckoutCanceling(false);
    }
  };

  const openReverseModal = () => {
    if (!activeMenu) return;
    setReverseReason('');
    setReverseBy('');
    setShowReverseModal(true);
  };

  const confirmReversePayment = async () => {
    if (!reverseReason.trim()) return;
    setShowReverseModal(false);
    setSendMsg('');
    try {
      const API = getApiBase();
      const id = activeMenu.external_id || activeMenu.id;
      const res = await fetch(`${API}/api/invoices/${encodeURIComponent(String(id))}/reverse-payment`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...getTenantHeaders() },
        body: JSON.stringify({ reason: reverseReason.trim(), reversed_by: reverseBy.trim() })
      });
      const data = await res.json();
      if (!res.ok) { setSendMsg(`❌ ${data.error || 'Reverse payment failed.'}`); return; }
      const updated = {
        ...activeMenu,
        amount_paid: 0,
        expected_amount_paid: 0,
        balance_due: Number(data.balance_due),
        status: data.status || 'UNPAID',
        payment_method: null,
        payment_ref: null,
        last_payment_method: null,
        last_payment_ref: null
      };
      if (typeof onPaymentReversed === 'function') onPaymentReversed(updated);
      setActiveMenu(updated);
      setSendMsg('✅ Payment reversed — invoice set back to ' + (data.status || 'UNPAID') + '.');
    } catch (e) {
      setSendMsg('❌ Network error — please try again.');
    }
  };

  return (
    <div style={{ marginTop: '20px' }}>
      <style>{`
        .jf-inv-card {
          display: flex;
          justify-content: space-between;
          align-items: center;
          gap: 12px;
          flex-wrap: wrap;
        }
        .jf-inv-left {
          min-width: 260px;
          flex: 1;
        }
        .jf-inv-right {
          display: flex;
          gap: 15px;
          align-items: center;
          flex-wrap: wrap;
          justify-content: flex-end;
        }
        @media (max-width: 520px) {
          .jf-inv-left { min-width: 100%; }
          .jf-inv-right { width: 100%; justify-content: space-between; }
        }
        .jf-hover-blue { transition: filter 120ms ease, transform 120ms ease; }
        .jf-hover-blue:hover {
          background: #FFC107 !important;
          color: #000 !important;
          border-color: #FFA500 !important;
          filter: brightness(1.02);
        }
        .jf-hover-blue:active { transform: translateY(1px); }
        .jf-hover-red:hover {
          background: #ff4444 !important;
          color: #fff !important;
          border-color: #000 !important;
          filter: brightness(1.1);
        }
        .jf-hover-red:active { transform: translateY(1px); }
        .jf-share-btn { transition: filter 120ms ease, transform 120ms ease; }
        .jf-share-btn:hover {
          background: #FFC107 !important;
          color: #000 !important;
          border-color: #FFA500 !important;
          filter: brightness(1.02);
        }
        .jf-share-btn:active { transform: translateY(1px); }
        .jf-manage-action {
          transition:
            background-color 120ms ease,
            color 120ms ease,
            border-color 120ms ease,
            filter 120ms ease,
            transform 120ms ease;
        }
        .jf-manage-action:hover { filter: brightness(1.05); }
        .jf-manage-action:active { transform: translateY(1px); }
        .jf-manage-convert:hover {
          background: #2DD4BF !important;
          color: #000 !important;
          border-color: #134E4A !important;
        }
        .jf-manage-copy:hover {
          background: #FFC107 !important;
          color: #000 !important;
          border-color: #FFA500 !important;
        }
        .jf-manage-delete:hover {
          background: #E7B888 !important;
          color: #000 !important;
          border-color: #000 !important;
        }
        .jf-manage-duplicate:hover {
          background: #9B6DFF !important;
          color: #000 !important;
          border-color: #3B1D6B !important;
        }
        .jf-manage-edit:hover {
          background: #E8F3EC !important;
          color: #14532D !important;
          border-color: #166534 !important;
        }
        .jf-manage-record:hover {
          background: #FFE66D !important;
          color: #000 !important;
          border-color: #A16207 !important;
        }
        .jf-manage-reverse:hover {
          background: #FB7185 !important;
          color: #000 !important;
          border-color: #4C0519 !important;
        }
        .jf-manage-take:hover {
          background: #4ADE80 !important;
          color: #000 !important;
          border-color: #14532D !important;
        }
        .jf-manage-view:hover {
          background: #FFC107 !important;
          color: #000 !important;
          border-color: #E8761D !important;
        }
        .jf-manage-close:hover {
          background: #374151 !important;
          color: #fff !important;
          border-color: #000 !important;
        }
      `}</style>

      {listControls}

      <div style={styles.listSurface}>
        {displayInvoices.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '40px', border: '4px dashed black', fontWeight: 900 }}>
            {isGlobalSearchActive ? 'NO INVOICES MATCH YOUR SEARCH' : 'NO INVOICES FOUND'}
          </div>
        ) : (
          displayInvoices.map(inv => {
          const invDate = fmtShortDate(getInvoiceDate(inv));
          const dueDate = fmtShortDate(getDueDate(inv));

          return (
            <div key={inv.id} style={styles.card} className="jf-inv-card">
              <div className="jf-inv-left">
                <div style={{ fontWeight: 900, fontSize: '22px' }}>
                  {inv.invoice_number} - {inv.company || inv.customer_name || 'No Name'}
                </div>
                <div style={{ marginTop: '8px', fontWeight: 900, lineHeight: 1.15 }}>
                  <div>INVOICE DATE: {invDate || '—'}</div>
                  <div>DUE DATE: {dueDate || '—'}</div>
                </div>
                <div style={{ fontSize: 'clamp(14px, 5vw, 20px)', fontWeight: 900, marginTop: '8px', whiteSpace: 'nowrap' }}>
                  {money(inv.total || 0)}
                </div>
                {Number(inv.amount_paid || 0) > 0 && Number(inv.balance_due || 0) > 0 && (
                  <div style={{ fontWeight: 900, marginTop: '6px' }}>
                    PAID: {money(inv.amount_paid)} | BALANCE: {money(inv.balance_due)}
                  </div>
                )}
              </div>

              <div className="jf-inv-right">
                {inv.first_viewed_at && (
                  <button
                    type="button"
                    title={viewedTitle(inv.first_viewed_at)}
                    style={styles.viewedBadge}
                    onClick={() => setViewedPopupText(viewedTitle(inv.first_viewed_at))}
                  >
                    <Eye size={17} strokeWidth={3} aria-hidden="true" />
                    VIEWED
                  </button>
                )}
                <div style={styles.badge(inv._display_status)}>{statusLabel(inv._display_status)}</div>
                <button
                  className="jf-hover-blue"
                  style={styles.manageBtn}
                  onClick={() => { setActiveMenu(inv); setSendMsg(''); setCopiedPublicUrl(''); }}
                >
                  MANAGE
                </button>
              </div>
            </div>
          );
          })
        )}
      </div>

      {viewedPopupText && (
        <div style={styles.viewedPopupOverlay}>
          <div style={styles.viewedPopup}>
            <div style={{ fontWeight: 900, fontSize: '22px', marginBottom: '12px' }}>
              INVOICE VIEWED
            </div>
            <div style={{ fontWeight: 800, lineHeight: 1.45, marginBottom: '18px' }}>
              {viewedPopupText}
            </div>
            <button
              className="jf-share-btn"
              style={{ ...styles.btn, backgroundColor: '#E8761D', marginBottom: 0 }}
              onClick={() => setViewedPopupText('')}
            >
              OK
            </button>
          </div>
        </div>
      )}

      {/* MANAGE MODAL */}
      {activeMenu && !showPayModal && (
        <div style={styles.overlay} onClick={() => { setActiveMenu(null); setSendMsg(''); setCopiedPublicUrl(''); }}>
          <div
            style={{
              ...styles.modal,
              backgroundColor: '#fff7ed',
              boxSizing: 'border-box',
              maxHeight: 'calc(100dvh - 24px)',
              overflowY: 'auto',
              overscrollBehavior: 'contain',
              WebkitOverflowScrolling: 'touch'
            }}
            onClick={e => e.stopPropagation()}
          >
            <h2 style={{ fontWeight: 900, marginTop: 0 }}>INVOICE #{activeMenu.invoice_number}</h2>

            {(deriveStatus(activeMenu) === 'unpaid' || deriveStatus(activeMenu) === 'overdue') && activeMenu.from_estimate_id && (
              <button
                className="jf-manage-action jf-manage-convert"
                style={{ ...styles.btn, backgroundColor: '#0F766E', color: '#FFFFFF', border: '4px solid #134E4A' }}
                onClick={() => setShowConvertBackModal(true)}
              >
                ↩️ CONVERT BACK TO ESTIMATE
              </button>
            )}

            {/* COPY INVOICE LINK — replaces EMAIL + TEXT buttons */}
            <button
              className="jf-manage-action jf-manage-copy"
              style={{
                ...styles.btn,
                backgroundColor: 'black',
                color: '#FFA500',
                border: '4px solid #E8761D',
                opacity: sendingLink ? 0.6 : 1,
                cursor: sendingLink ? 'not-allowed' : 'pointer'
              }}
              onClick={handleCopyLink}
              disabled={sendingLink}
            >
              {sendingLink ? '⏳ GETTING LINK...' : '🔗 COPY INVOICE LINK'}
            </button>

            {sendMsg && (
              <div style={{
                padding: '12px',
                marginBottom: '10px',
                border: '3px solid black',
                fontWeight: 900,
                fontSize: '14px',
                backgroundColor: sendMsg.startsWith('✅') ? '#dcfce7' : '#fee2e2'
              }}>
                {sendMsg}
              </div>
            )}

            {copiedPublicUrl && (
              <>
                <div style={styles.shareDimmer} onClick={e => e.stopPropagation()} />
                <div style={styles.sharePopup}>
                  <h3 style={{ margin: '0 0 8px', textAlign: 'center', fontWeight: 900, fontSize: '22px' }}>
                    LINK READY TO SHARE!
                  </h3>
                  {sendMsg.startsWith('NO CUSTOMER') && (
                    <div style={{ padding: '10px', marginBottom: '10px', border: '3px solid black', backgroundColor: '#fee2e2', fontWeight: 900, textAlign: 'center' }}>
                      {sendMsg}
                    </div>
                  )}
                  <button
                    className="jf-share-btn"
                    style={{ ...styles.btn, backgroundColor: '#39FF14', marginBottom: '8px' }}
                    onClick={handleTextCustomer}
                  >
                    TEXT NOW
                  </button>
                  <button
                    className="jf-share-btn"
                    style={{ ...styles.btn, backgroundColor: '#E8761D', marginBottom: '8px' }}
                    onClick={handleEmailCustomer}
                  >
                    EMAIL NOW
                  </button>
                  <button
                    className="jf-share-btn"
                    style={{ ...styles.btn, backgroundColor: 'black', color: 'white', marginBottom: 0 }}
                    onClick={() => setCopiedPublicUrl('')}
                  >
                    CLOSE NOW
                  </button>
                  <div style={{ marginTop: '10px', fontSize: '13px', fontWeight: 800, lineHeight: 1.45, textAlign: 'center' }}>
                    <div>
                      Texting requires a mobile device<br />
                      or a configured SMS app.
                    </div>
                    <div style={{ marginTop: '8px' }}>
                      Your link is still on your clipboard<br />
                      and ready to paste anywhere.
                    </div>
                    <div style={{ marginTop: '8px' }}>
                      Paste it into any email or messaging app.
                    </div>
                  </div>
                </div>
              </>
            )}

            <button
              className="jf-manage-action jf-manage-delete"
              style={{ ...styles.btn, backgroundColor: '#754C38', color: '#fff7ed', border: '4px solid black' }}
              onClick={() => {
                setDeleteReason('');
                setRestoreInventory(null);
                setShowDeleteModal(true);
              }}
            >
              🗑️ DELETE
            </button>

            <button
              className="jf-manage-action jf-manage-duplicate"
              style={{ ...styles.btn, backgroundColor: '#6F42C1', color: 'white', border: '4px solid #3B1D6B' }}
              onClick={() => {
                if (typeof onDuplicate !== 'function') return;
                onDuplicate(activeMenu);
                setActiveMenu(null);
                setSendMsg('');
                setCopiedPublicUrl('');
              }}
            >
              DUPLICATE NOW
            </button>

            <button
              className="jf-manage-action jf-manage-edit"
              style={{ ...styles.btn, backgroundColor: '#D9F2D0', color: '#14532D', border: '4px solid #166534' }}
              onClick={() => { onEdit(activeMenu); setActiveMenu(null); setCopiedPublicUrl(''); }}
            >
              ✏️ EDIT INVOICE
            </button>

            {deriveStatus(activeMenu) !== 'paid' && (
              <button
                className="jf-manage-action jf-manage-record"
                style={{ ...styles.btn, backgroundColor: '#FFD400', color: 'black', border: '4px solid #A16207' }}
                onClick={() => {
                  setPaymentError('');
                  setPaymentOutstandingCents(getOutstandingCents(activeMenu));
                  setShowPayModal(true);
                }}
              >
                💳 RECORD PAYMENT (PARTIAL OK)
              </button>
            )}

            {['paid', 'partial'].includes(deriveStatus(activeMenu)) && (
              <button
                className="jf-manage-action jf-manage-reverse"
                style={{ ...styles.btn, backgroundColor: '#9F1239', color: 'white', border: '4px solid #4C0519' }}
                onClick={openReverseModal}
              >
                🔄 REVERSE PAYMENT
              </button>
            )}

            {deriveStatus(activeMenu) !== 'paid' && (
              <button
                className="jf-manage-action jf-manage-take"
                style={{ ...styles.btn, backgroundColor: '#166534', color: 'white', border: '4px solid #14532D' }}
                onClick={() => {
                  const bal = Number(activeMenu.balance_due ?? (Number(activeMenu.total || 0) - Number(activeMenu.amount_paid || 0)));
                  setCheckoutAmount(Number(bal * 0.5).toFixed(2));
                  setCheckoutFeedback(null);
                  setActiveCheckoutConflict(null);
                  setShowCheckoutModal(true);
                }}
              >
                💳 TAKE PAYMENT NOW
              </button>
            )}

            <button
              className="jf-manage-action jf-manage-view"
              style={{ ...styles.btn, backgroundColor: '#E8761D', color: 'black', border: '4px solid #9A3412' }}
              onClick={() => {
                if (typeof onViewPrint === 'function') onViewPrint(activeMenu);
                else alert('Hook onViewPrint(invoice) from invoices.js to open the print view.');
              }}
            >
              📄 VIEW / PRINT
            </button>

            <button
              className="jf-manage-action jf-manage-close"
              style={{ ...styles.btn, backgroundColor: 'black', color: 'white', border: '4px solid black' }}
              onClick={() => { setActiveMenu(null); setSendMsg(''); setCopiedPublicUrl(''); }}
            >
              CLOSE
            </button>
          </div>
        </div>
      )}

      {/* PAYMENT MODAL */}
      {showPayModal && activeMenu && (
        <div style={styles.overlay}>
          <div style={styles.modal}>
            <h2 style={{ fontWeight: 900 }}>RECORD PAYMENT</h2>

            <div style={{ display: 'grid', gap: '10px' }}>
              <div style={{ fontWeight: 900 }}>
                TOTAL: {money(activeMenu.total || 0)} | PAID: {money(activeMenu.amount_paid || 0)} | BALANCE: {money(fromCents(paymentOutstandingCents ?? getOutstandingCents(activeMenu)))}
              </div>

              {paymentError && (
                <div style={{ padding: '12px', border: '3px solid black', backgroundColor: '#fee2e2', fontWeight: 900 }}>
                  {paymentError}
                </div>
              )}

              <input
                style={styles.input}
                placeholder="PAYMENT AMOUNT"
                type="number"
                min="0.01"
                step="0.01"
                max={fromCents(paymentOutstandingCents ?? getOutstandingCents(activeMenu))}
                value={payData.amount}
                onChange={e => {
                  setPaymentError('');
                  setPayData({ ...payData, amount: e.target.value });
                }}
              />

              <select
                style={styles.input}
                value={payData.method}
                onChange={e => setPayData({ ...payData, method: e.target.value })}
              >
                <option>Cash</option>
                <option>Check</option>
                <option>Card</option>
                <option>Venmo</option>
                <option>Zelle</option>
                <option>Other</option>
              </select>

              <input
                style={styles.input}
                placeholder="Check # / Ref # (optional)"
                value={payData.ref}
                onChange={e => setPayData({ ...payData, ref: e.target.value })}
              />
            </div>

            <button
              style={{ ...styles.btn, backgroundColor: '#E8761D', marginTop: '20px' }}
              onClick={submitPayment}
              disabled={paymentSubmitting}
            >
              {paymentSubmitting ? 'RECORDING...' : 'CONFIRM'}
            </button>

            <button
              style={{ ...styles.btn, backgroundColor: 'black', color: 'white' }}
              onClick={() => {
                setShowPayModal(false);
                setPayData({ method: 'Cash', ref: '', amount: '' });
                setPaymentError('');
                setPaymentOutstandingCents(null);
              }}
              disabled={paymentSubmitting}
            >
              CANCEL
            </button>
          </div>
        </div>
      )}

      {/* REVERSE PAYMENT MODAL */}
      {showReverseModal && activeMenu && (
        <div style={styles.overlay}>
          <div style={styles.modal}>
            <h2 style={{ fontWeight: 900, marginTop: 0 }}>REVERSE PAYMENT</h2>
            <div style={{ fontWeight: 900, marginBottom: '16px', fontSize: '14px' }}>
              Invoice #{activeMenu.invoice_number} — {money(activeMenu.amount_paid || 0)} paid
            </div>

            <div style={{ display: 'grid', gap: '10px' }}>
              <div>
                <div style={{ fontWeight: 900, fontSize: '12px', marginBottom: '6px', textTransform: 'uppercase' }}>
                  Reason for Reversal <span style={{ color: 'red' }}>*</span>
                </div>
                <textarea
                  style={{ ...styles.input, height: '80px', resize: 'vertical' }}
                  placeholder="Required — enter reason for reversing this payment"
                  value={reverseReason}
                  onChange={e => setReverseReason(e.target.value)}
                />
              </div>

              <div>
                <div style={{ fontWeight: 900, fontSize: '12px', marginBottom: '6px', textTransform: 'uppercase' }}>
                  Reversed By
                </div>
                <input
                  style={styles.input}
                  placeholder="Name or email"
                  value={reverseBy}
                  onChange={e => setReverseBy(e.target.value)}
                />
              </div>
            </div>

            <button
              style={{
                ...styles.btn,
                backgroundColor: reverseReason.trim() ? '#CC0000' : '#ccc',
                color: 'white',
                border: '4px solid black',
                marginTop: '20px',
                cursor: reverseReason.trim() ? 'pointer' : 'not-allowed'
              }}
              onClick={confirmReversePayment}
              disabled={!reverseReason.trim()}
            >
              🔄 CONFIRM REVERSAL
            </button>

            <button
              style={{ ...styles.btn, backgroundColor: 'black', color: 'white' }}
              onClick={() => setShowReverseModal(false)}
            >
              CANCEL
            </button>
          </div>
        </div>
      )}

      {/* DELETE INVOICE MODAL */}
      {showDeleteModal && activeMenu && (
        <div style={styles.overlay}>
          <div style={styles.modal}>
            <h2 style={{ fontWeight: 900, marginTop: 0 }}>DELETE INVOICE</h2>
            <div style={{ fontWeight: 900, marginBottom: '16px', fontSize: '14px' }}>
              Invoice #{activeMenu.invoice_number} — {money(activeMenu.total || 0)}
            </div>
            <div style={{ display: 'grid', gap: '10px' }}>
              <div>
                <div style={{ fontWeight: 900, fontSize: '12px', marginBottom: '6px', textTransform: 'uppercase' }}>
                  Reason for Deletion <span style={{ color: 'red' }}>*</span>
                </div>
                <textarea
                  style={{ ...styles.input, height: '80px', resize: 'vertical' }}
                  placeholder="Required — enter reason for deleting this invoice"
                  value={deleteReason}
                  onChange={e => setDeleteReason(e.target.value)}
                />
              </div>
              {hasInventorySelectedParts && (
                <fieldset style={{ border: 0, margin: 0, padding: 0 }}>
                  <legend style={{ fontWeight: 900, fontSize: '12px', marginBottom: '6px', textTransform: 'uppercase' }}>
                    Return deducted inventory items to inventory? <span style={{ color: 'red' }}>*</span>
                  </legend>
                  <label style={{ display: 'block', fontWeight: 800, marginBottom: '6px' }}>
                    <input
                      type="radio"
                      name="restore-inventory"
                      checked={restoreInventory === true}
                      onChange={() => setRestoreInventory(true)}
                    />{' '}
                    YES — Return items to inventory.
                  </label>
                  <label style={{ display: 'block', fontWeight: 800 }}>
                    <input
                      type="radio"
                      name="restore-inventory"
                      checked={restoreInventory === false}
                      onChange={() => setRestoreInventory(false)}
                    />{' '}
                    NO — Do not return items to inventory.
                  </label>
                </fieldset>
              )}
            </div>
            <button
              style={{
                ...styles.btn,
                backgroundColor: canConfirmDelete ? '#CC0000' : '#ccc',
                color: 'white',
                border: '4px solid black',
                marginTop: '20px',
                cursor: canConfirmDelete ? 'pointer' : 'not-allowed'
              }}
              onClick={() => {
                if (!canConfirmDelete) return;
                onDelete(activeMenu.id, deleteReason.trim(), currentUser?.email || 'Unknown', restoreInventory);
                setShowDeleteModal(false);
                setRestoreInventory(null);
                setActiveMenu(null);
                setCopiedPublicUrl('');
              }}
              disabled={!canConfirmDelete}
            >
              🗑️ CONFIRM DELETE
            </button>
            <button
              style={{ ...styles.btn, backgroundColor: 'black', color: 'white' }}
              onClick={() => {
                setRestoreInventory(null);
                setShowDeleteModal(false);
              }}
            >
              CANCEL
            </button>
          </div>
        </div>
      )}

      {/* CHECKOUT AMOUNT MODAL */}
      {showCheckoutModal && activeMenu && (
        <div
          style={styles.overlay}
          onClick={() => {
            if (!checkoutSubmitting && !checkoutCanceling) setShowCheckoutModal(false);
          }}
        >
          <div style={styles.modal} onClick={e => e.stopPropagation()}>
            <h2 style={{ fontWeight: 900, marginTop: 0 }}>TAKE PAYMENT NOW</h2>
            <div style={{ fontWeight: 900, marginBottom: '16px', fontSize: '14px' }}>
              Invoice #{activeMenu.invoice_number} — Balance Due: {money(activeMenu.balance_due ?? (Number(activeMenu.total || 0) - Number(activeMenu.amount_paid || 0)))}
            </div>

            {checkoutFeedback && (
              <div style={{
                padding: '12px',
                marginBottom: '10px',
                border: '3px solid black',
                backgroundColor: checkoutFeedback.success ? '#dcfce7' : '#fee2e2',
                fontWeight: 900
              }}>
                {checkoutFeedback.message}
              </div>
            )}

            {activeCheckoutConflict?.code === 'ACTIVE_CHECKOUT_AMOUNT_CONFLICT' &&
              activeCheckoutConflict.can_cancel_active_session === true && (
                <button
                  className="jf-hover-red"
                  style={{ ...styles.btn, backgroundColor: '#CC0000', color: 'white', border: '4px solid black' }}
                  onClick={cancelActiveCheckout}
                  disabled={checkoutCanceling || checkoutSubmitting}
                >
                  {checkoutCanceling ? 'CANCELING PAYMENT SESSION...' : 'CANCEL EXISTING CHECKOUT'}
                </button>
              )}

            <button
              className="jf-hover-blue"
              style={{ ...styles.btn, backgroundColor: '#85bb65', color: '#FFD700', border: '4px solid #5a8a3f' }}
              onClick={() => handleTakePayment()}
              disabled={checkoutSubmitting || checkoutCanceling}
            >
              {checkoutSubmitting ? '⏳ STARTING CHECKOUT...' : `💳 FULL PAYMENT — ${money(activeMenu.balance_due ?? (Number(activeMenu.total || 0) - Number(activeMenu.amount_paid || 0)))}`}
            </button>

            <div style={{ border: '4px solid black', padding: '16px', marginBottom: '10px' }}>
              <div style={{ fontWeight: 900, fontSize: '14px', marginBottom: '10px', textTransform: 'uppercase' }}>
                Deposit / Partial Amount
              </div>
              <input
                style={styles.input}
                type="number"
                min="0.01"
                step="0.01"
                value={checkoutAmount}
                onChange={e => setCheckoutAmount(e.target.value)}
                disabled={checkoutSubmitting || checkoutCanceling}
              />
              <button
                className="jf-hover-blue"
                style={{ ...styles.btn, backgroundColor: 'yellow', marginTop: '10px', marginBottom: 0 }}
                onClick={() => {
                  const cents = toCents(checkoutAmount);
                  if (!cents || cents <= 0) {
                    setSendMsg('❌ Enter a deposit amount.');
                    setShowCheckoutModal(false);
                    return;
                  }
                  handleTakePayment(cents);
                }}
                disabled={checkoutSubmitting || checkoutCanceling}
              >
                💳 CHARGE ${checkoutAmount || '0.00'} DEPOSIT
              </button>
            </div>

            <button
              className="jf-hover-blue"
              style={{ ...styles.btn, backgroundColor: 'black', color: 'white' }}
              onClick={() => {
                setShowCheckoutModal(false);
                setCheckoutFeedback(null);
                setActiveCheckoutConflict(null);
              }}
              disabled={checkoutSubmitting || checkoutCanceling}
            >
              CANCEL
            </button>
          </div>
        </div>
      )}

      {/* CONVERT BACK TO ESTIMATE MODAL */}
      {showConvertBackModal && activeMenu && (
        <div style={styles.overlay}>
          <div style={styles.modal}>
            <h2 style={{ fontWeight: 900, marginTop: 0 }}>CONVERT BACK TO ESTIMATE</h2>
            <div style={{ fontWeight: 900, marginBottom: '16px', fontSize: '14px' }}>
              Invoice #{activeMenu.invoice_number} — {money(activeMenu.total || 0)}
            </div>
            <div style={{ fontWeight: 700, fontSize: '14px', marginBottom: '20px', lineHeight: 1.6 }}>
              This will permanently delete this invoice and restore the linked estimate to <strong>draft</strong> status.
            </div>
            <button
              style={{
                ...styles.btn,
                backgroundColor: '#0066cc',
                color: 'white',
                border: '4px solid black',
                marginTop: '4px'
              }}
              onClick={() => {
                onConvertBackToEstimate(activeMenu);
                setShowConvertBackModal(false);
                setActiveMenu(null);
                setCopiedPublicUrl('');
              }}
            >
              ↩️ CONFIRM — CONVERT BACK
            </button>
            <button
              style={{ ...styles.btn, backgroundColor: 'black', color: 'white' }}
              onClick={() => setShowConvertBackModal(false)}
            >
              CANCEL
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default InvoiceList;
